// Utility functions for connecting to exchange APIs
// This is where you would implement the actual API calls

import axios from 'axios';
import CryptoJS from 'crypto-js';

// Binance API functions
export const fetchBinanceBalances = async (apiKey, secret) => {
  // Get account information from Binance
  const timestamp = Date.now();
  const recvWindow = 5000; // 5 seconds
  
  // Prepare parameters
  const params = `timestamp=${timestamp}&recvWindow=${recvWindow}`;
  const signature = CryptoJS.HmacSHA256(params, secret).toString(CryptoJS.enc.Hex);
  
  try {
    // First, get account details to retrieve balances
    const response = await axios.get('https://api.binance.com/api/v3/account', {
      headers: {
        'X-MBX-APIKEY': apiKey
      },
      params: {
        timestamp,
        recvWindow,
        signature
      }
    });
    
    // Extract balances that have non-zero amounts
    const balances = response.data.balances
      .filter(balance => 
        parseFloat(balance.free) > 0 || parseFloat(balance.locked) > 0
      )
      .map(balance => ({
        asset: balance.asset,
        free: balance.free,
        locked: balance.locked
      }));
    
    // Get prices for each asset to calculate USD value
    const pricesResponse = await axios.get('https://api.binance.com/api/v3/ticker/price');
    const prices = {};
    pricesResponse.data.forEach(item => {
      prices[item.symbol] = parseFloat(item.price);
    });
    
    // Calculate USD values and return formatted data
    return balances.map(balance => {
      // Try to find the price for this asset against USDT or BTC
      let price = 0;
      // First try to find direct USDT pair (e.g., BTCUSDT)
      if (prices[balance.asset + 'USDT']) {
        price = prices[balance.asset + 'USDT'];
      } 
      // Then try BUSD pair (e.g., BTCBUSD)
      else if (prices[balance.asset + 'BUSD']) {
        price = prices[balance.asset + 'BUSD'];
      }
      // Then try BTC pair (e.g., BTCUSDT, then convert using BTC price)
      else if (prices[balance.asset + 'BTC'] && prices['BTCUSDT']) {
        price = prices[balance.asset + 'BTC'] * prices['BTCUSDT'];
      }
      
      const totalAmount = parseFloat(balance.free) + parseFloat(balance.locked);
      const usdValue = totalAmount * price;
      
      return {
        asset: balance.asset,
        free: balance.free,
        locked: balance.locked,
        usdValue: usdValue
      };
    }).filter(asset => asset.usdValue > 0.01); // Only include assets with value > $0.01
    
  } catch (error) {
    console.error('Error fetching Binance balances:', error.response?.data || error.message);
    throw error;
  }
};

// OKX API functions
export const fetchOkxBalances = async (apiKey, secret, passphrase) => {
  // OKX uses a different authentication method with timestamp, method, request path, and body
  const timestamp = new Date().toISOString();
  const method = 'GET';
  const requestPath = '/api/v5/account/balance';
  
  // Create prehash string: timestamp + method + request path
  const preHash = timestamp + method + requestPath;
  const signature = CryptoJS.HmacSHA256(preHash, secret).toString(CryptoJS.enc.Base64);
  
  try {
    const response = await axios.get('https://www.okx.com/api/v5/account/balance', {
      headers: {
        'Content-Type': 'application/json',
        'OK-ACCESS-KEY': apiKey,
        'OK-ACCESS-SIGN': signature,
        'OK-ACCESS-TIMESTAMP': timestamp,
        'OK-ACCESS-PASSPHRASE': passphrase
      }
    });
    
    // Process the response
    if (response.data.code === '0' && response.data.data) {
      const balances = response.data.data[0].details
        .filter(detail => 
          parseFloat(detail.availBal) > 0 || parseFloat(detail.frozenBal) > 0
        )
        .map(detail => ({
          ccy: detail.ccy,
          bal: detail.availBal,
          frozen: detail.frozenBal,
          eq: detail.cashBal // cashBal represents the equivalent value in USD
        }));
      
      // Get current prices to calculate accurate USD values
      const pricesResponse = await axios.get('https://www.okx.com/api/v5/market/tickers?instType=SPOT');
      const prices = {};
      if (pricesResponse.data.code === '0' && pricesResponse.data.data) {
        pricesResponse.data.data.forEach(item => {
          // Extract base currency from instrument ID (e.g., BTC-USDT -> BTC)
          const [baseCcy] = item.instId.split('-');
          if (!prices[baseCcy]) {
            prices[baseCcy] = parseFloat(item.last);
          }
        });
      }
      
      // Calculate USD values based on current prices
      return balances.map(balance => {
        const price = prices[balance.ccy] || parseFloat(balance.eq) / parseFloat(balance.bal) || 0;
        const totalAmount = parseFloat(balance.bal) + parseFloat(balance.frozen);
        const usdValue = totalAmount * price;
        
        return {
          ccy: balance.ccy,
          bal: balance.bal,
          eq: usdValue // Replace with calculated value
        };
      }).filter(asset => asset.eq > 0.01); // Only include assets with value > $0.01
    } else {
      throw new Error(response.data.msg || 'Error fetching OKX balances');
    }
  } catch (error) {
    console.error('Error fetching OKX balances:', error.response?.data || error.message);
    throw error;
  }
};

// Hyperliquid API functions
export const fetchHyperliquidBalances = async (apiKey, privateKey) => {
  // Hyperliquid uses a different approach - we need to make a post request to their API
  try {
    // Get user's spot balances
    const userInfoPayload = {
      method: "info",
      params: {
        user: apiKey,
        type: "spot"
      },
      id: 1
    };
    
    const response = await axios.post('https://api.hyperliquid.xyz/info', userInfoPayload);
    
    if (response.data && response.data.allSpot) {
      const balances = response.data.allSpot
        .filter(asset => parseFloat(asset.total) > 0)
        .map(asset => {
          return {
            coin: asset.coin,
            amount: asset.total,
            usdValue: parseFloat(asset.usd) || 0
          };
        });
      
      return balances.filter(asset => asset.usdValue > 0.01); // Only include assets with value > $0.01
    } else {
      throw new Error('Error fetching Hyperliquid balances');
    }
  } catch (error) {
    console.error('Error fetching Hyperliquid balances:', error.response?.data || error.message);
    throw error;
  }
};

// Aster API functions
export const fetchAsterBalances = async (apiKey, secret) => {
  // Aster API implementation (placeholder - would need actual API documentation)
  // For now, using a typical exchange API pattern
  
  // Since I don't have specific Aster API documentation, I'll create a typical implementation
  // that follows common patterns used by crypto exchanges
  try {
    // Create a signature for API authentication
    const timestamp = Date.now().toString();
    const params = `timestamp=${timestamp}`;
    
    // This is a placeholder - in a real implementation, we would use Aster's actual API endpoints
    // For demonstration purposes, I'll create a mock that simulates a real API response
    const mockResponse = {
      code: 0,
      data: [
        { asset: 'ASTR', available: '5000.000000', locked: '0.000000', usdValue: 400.00 },
        { asset: 'ETH', available: '2.500000', locked: '0.500000', usdValue: 6400.00 },
      ]
    };
    
    // In a real implementation, we would make an actual API call:
    /*
    const signature = CryptoJS.HmacSHA256(params, secret).toString(CryptoJS.enc.Hex);
    
    const response = await axios.get('https://api.aster.example.com/v1/account/balances', {
      headers: {
        'X-API-KEY': apiKey,
        'X-SIGNATURE': signature,
        'X-TIMESTAMP': timestamp
      }
    });
    
    if (response.data.code === 0) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Error fetching Aster balances');
    }
    */
    
    // Return mock data for now - this would be replaced with real API response
    return mockResponse.data;
  } catch (error) {
    console.error('Error fetching Aster balances:', error.message);
    throw error;
  }
};

// DeFi wallet functions
export const fetchDefiBalances = async (walletAddress) => {
  // This is a placeholder - implement actual blockchain explorer API call
  // Use Etherscan, Polygonscan, or other blockchain explorers
  
  // For demo purposes, returning mock data
  return [
    { token: 'UNI', balance: '45.000000', usdValue: 306.00 },
    { token: 'LINK', balance: '30.000000', usdValue: 426.00 },
  ];
};

// Helper function to normalize asset data across different exchanges
export const normalizeAssetData = (assets, source) => {
  return assets.map(asset => {
    let normalized = {
      symbol: '',
      amount: 0,
      price: 0,
      value: 0,
      source: source
    };

    // Map different exchange formats to our standard format
    if (source === 'binance') {
      normalized.symbol = asset.asset;
      normalized.amount = parseFloat(asset.free) + parseFloat(asset.locked);
      normalized.value = asset.usdValue || 0;
      normalized.price = (normalized.value / normalized.amount) || 0;
    } else if (source === 'okx') {
      normalized.symbol = asset.ccy;
      normalized.amount = parseFloat(asset.bal) || 0;
      normalized.value = parseFloat(asset.eq) || 0;
      normalized.price = normalized.value / normalized.amount || 0;
    } else if (source === 'hyperliquid') {
      normalized.symbol = asset.coin;
      normalized.amount = parseFloat(asset.amount) || 0;
      normalized.value = asset.usdValue || 0;
      normalized.price = normalized.value / normalized.amount || 0;
    } else if (source === 'aster') {
      normalized.symbol = asset.asset;
      normalized.amount = parseFloat(asset.available) || 0;
      normalized.value = asset.usdValue || 0;
      normalized.price = normalized.value / normalized.amount || 0;
    } else if (source === 'defi') {
      normalized.symbol = asset.token;
      normalized.amount = parseFloat(asset.balance) || 0;
      normalized.value = asset.usdValue || 0;
      normalized.price = normalized.value / normalized.amount || 0;
    }

    return normalized;
  });
};

// Function to get all assets from enabled exchanges
export const fetchAllAssets = async (exchanges) => {
  let allAssets = [];

  if (exchanges.binance.enabled && exchanges.binance.apiKey && exchanges.binance.secret) {
    try {
      const binanceAssets = await fetchBinanceBalances(exchanges.binance.apiKey, exchanges.binance.secret);
      allAssets = [...allAssets, ...normalizeAssetData(binanceAssets, 'binance')];
    } catch (error) {
      console.error('Error fetching Binance data:', error);
    }
  }

  if (exchanges.okx.enabled && exchanges.okx.apiKey && exchanges.okx.secret && exchanges.okx.passphrase) {
    try {
      const okxAssets = await fetchOkxBalances(exchanges.okx.apiKey, exchanges.okx.secret, exchanges.okx.passphrase);
      allAssets = [...allAssets, ...normalizeAssetData(okxAssets, 'okx')];
    } catch (error) {
      console.error('Error fetching OKX data:', error);
    }
  }

  if (exchanges.hyperliquid.enabled && exchanges.hyperliquid.apiKey && exchanges.hyperliquid.secret) {
    try {
      const hyperliquidAssets = await fetchHyperliquidBalances(exchanges.hyperliquid.apiKey, exchanges.hyperliquid.secret);
      allAssets = [...allAssets, ...normalizeAssetData(hyperliquidAssets, 'hyperliquid')];
    } catch (error) {
      console.error('Error fetching Hyperliquid data:', error);
    }
  }

  if (exchanges.aster.enabled && exchanges.aster.apiKey && exchanges.aster.secret) {
    try {
      const asterAssets = await fetchAsterBalances(exchanges.aster.apiKey, exchanges.aster.secret);
      allAssets = [...allAssets, ...normalizeAssetData(asterAssets, 'aster')];
    } catch (error) {
      console.error('Error fetching Aster data:', error);
    }
  }

  if (exchanges.defiWallets.length > 0) {
    for (const wallet of exchanges.defiWallets) {
      try {
        const defiAssets = await fetchDefiBalances(wallet);
        allAssets = [...allAssets, ...normalizeAssetData(defiAssets, 'defi')];
      } catch (error) {
        console.error(`Error fetching DeFi wallet data for ${wallet}:`, error);
      }
    }
  }

  return allAssets;
};